/* eslint react/jsx-max-depth: ["error", {max: 5}] -- icon tile rendered inline inside content shell */
import type { ReactElement } from "react";

import { MedicalReviewState } from "@/constants/Onboarding";
import { BaseActiveOnboardingCardContent } from "@/ui/components/onboarding/v2/cards/BaseActiveOnboardingCardContent";
import { BaseOnboardingCard } from "@/ui/components/onboarding/v2/cards/BaseOnboardingCard";
import { OnboardingVideoPreviewCard } from "@/ui/components/onboarding/v2/cards/OnboardingVideoPreviewCard";
import { getMedicalReviewContent } from "@/ui/components/onboarding/v2/content/MedicalReviewContent";
import { Button, Icon } from "@/ui/core";
import type { FAIconName } from "@/ui/core";
import { Box } from "@/ui/layout";
import {
  OnboardingIllustrationSize,
  OnboardingIllustrationSizeConfig,
} from "@/ui/theme/icons/illustrations/onboarding";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

const TileSize =
  OnboardingIllustrationSizeConfig[OnboardingIllustrationSize.Default];

interface StyleConfig {
  captionColor: ColorToken;
  cardBackgroundColor: ColorToken;
  cardBorderColor: ColorToken;
  iconBackgroundColor: ColorToken;
  iconColor: ColorToken;
}

const StyleConfigRecord: Record<MedicalReviewState, StyleConfig> = {
  [MedicalReviewState.Approved]: {
    captionColor: ColorToken.Primary100,
    cardBackgroundColor: ColorToken.Primary10,
    cardBorderColor: ColorToken.Primary100,
    iconBackgroundColor: ColorToken.NotificationSuccessBackground,
    iconColor: ColorToken.NotificationSuccessText,
  },
  [MedicalReviewState.NotApproved]: {
    captionColor: ColorToken.Secondary70,
    cardBackgroundColor: ColorToken.Secondary5,
    cardBorderColor: ColorToken.AccentPeriwinkle,
    iconBackgroundColor: ColorToken.Secondary10,
    iconColor: ColorToken.Secondary50,
  },
  [MedicalReviewState.Pending]: {
    captionColor: ColorToken.TertiaryMarigoldText,
    cardBackgroundColor: ColorToken.NeutralBackgroundPrimary,
    cardBorderColor: ColorToken.TertiaryMarigoldText,
    iconBackgroundColor: ColorToken.TertiaryMarigoldBackground,
    iconColor: ColorToken.TertiaryMarigoldBase,
  },
};

interface IconTileProps extends ViewWithTestID {
  backgroundColor: ColorToken;
  color: ColorToken;
  iconName: FAIconName;
}

const IconTile = ({
  backgroundColor,
  color,
  iconName,
  testID,
}: IconTileProps) => (
  <Box
    alignItems="center"
    backgroundColor={backgroundColor}
    borderRadius={TileSize.borderRadius}
    height={TileSize.dimensions}
    justifyContent="center"
    width={TileSize.dimensions}
  >
    <Icon color={color} name={iconName} size="lg" testID={testID} />
  </Box>
);

interface MedicalReviewCardProps extends ViewWithTestID {
  onActionPress: () => void;
  patientName?: string;
  state: MedicalReviewState;
}

const MedicalReviewCard = ({
  onActionPress,
  patientName,
  state,
  testID,
}: MedicalReviewCardProps): ReactElement => {
  const content = getMedicalReviewContent({ patientName, state });
  const tone = StyleConfigRecord[state];

  return (
    <BaseOnboardingCard
      backgroundColor={tone.cardBackgroundColor}
      borderColor={tone.cardBorderColor}
      borderRadius="large"
      hasShadow
      testID={testID}
    >
      <BaseActiveOnboardingCardContent
        action={
          <Button
            isFullWidth
            leftIcon={content.button.leftIcon}
            onPress={onActionPress}
            rightIcon={content.button.rightIcon}
            testID={joinTestIDs(testID, "button")}
            title={content.button.title}
            variant={content.button.variant}
          />
        }
        caption={content.caption}
        captionColor={tone.captionColor}
        description={content.description}
        duration={content.estimate}
        icon={
          <IconTile
            backgroundColor={tone.iconBackgroundColor}
            color={tone.iconColor}
            iconName={content.iconName}
            testID={joinTestIDs(testID, "tile")}
          />
        }
        testID={testID}
        title={content.title}
      >
        {state === MedicalReviewState.Pending && (
          <OnboardingVideoPreviewCard testID={testID} />
        )}
      </BaseActiveOnboardingCardContent>
    </BaseOnboardingCard>
  );
};

export type { MedicalReviewCardProps };
export { MedicalReviewCard };
